import React, { PropsWithChildren, FC, useState, useEffect, useCallback } from "react";

import {
    Accordion,
    Button,
    EmailInput,
    Input,
    LoadingSpinner,
    PageAlert,
    PhoneNumberInput,
    SmartDropdown,
    URLInput,
    validateEmail,
    validatePhoneNumber,
} from "@xapp/components";

import { TEMPLATES } from "constants/templates";

import { WidgetColorPicker } from "components/WidgetThemeBuilder/WidgetColorPicker";

import { CreateProps } from "../models";

export interface ConfigureAndCreateStepProps {
    /**
     * Site data used to prepopulate fields
     */
    initialProps?: CreateProps;
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * Optional loading message to display to the user
     */
    loadingMessage?: string;
    /**
     * Is the app being created
     */
    creating?: boolean;
    /**
     * Status message displayed to the user to keep them updated on the creation process
     */
    creatingStatusMessage?: string;
    /**
     * Error message to display to the user if something goes wrong.
     */
    error?: { error: string, message: string };
    /**
     * Show beta features
     */
    beta?: boolean;
    /**
     * The user has requested to create an app based on the provided properties.
     */
    onCreate(props: CreateProps): void;
    /**
     * The user wants to go back
     */
    onBack?(): void;
}

export const ConfigureAndCreateStep: FC<PropsWithChildren<ConfigureAndCreateStepProps>> = props => {

    const {
        className,
        creating,
        creatingStatusMessage,
        error,
        initialProps,
        loadingMessage,
        onBack,
        onCreate,
        style,
    } = props;

    // We get initial props from website loads or
    const [createProps, setCreateProps] = useState<CreateProps>(initialProps);

    const [activeAccordian, setActiveAccordian] = useState<string>("about");

    // validity checks
    const [validEmail, setValidEmail] = useState<boolean>(createProps?.leadsContact ? validateEmail(createProps.leadsContact) : false);
    const [validPhone, setValidPhone] = useState<boolean>(createProps?.leadsContactPhone ? validatePhoneNumber(createProps.leadsContactPhone) : false);

    useEffect(() => {
        if (initialProps) {
            setCreateProps({
                ...initialProps
            });

        }
    }, [initialProps, setCreateProps])

    const createSetter = useCallback((key: keyof Omit<CreateProps, "template">) => {
        return (value: string, valid?: boolean) => {
            if (typeof valid === "boolean") {
                switch (key) {
                    case "leadsContact":
                        setValidEmail(valid);
                        break;
                    case "leadsContactPhone":
                        setValidPhone(valid);
                        break;
                    default:
                }
            }
            setCreateProps({
                ...createProps,
                [key]: value
            })
        }
    }, [setCreateProps, createProps, setValidEmail, setValidPhone]);

    const handleAccordianChange = useCallback((accordian: string, event: React.SyntheticEvent, expanded: boolean) => {
        if (expanded) {
            setActiveAccordian(accordian);
        } else {
            if (accordian === "about") {
                setActiveAccordian("train")
            } else if (accordian === "train") {
                setActiveAccordian("theme");
            } else if (accordian === "theme") {
                setActiveAccordian("scheduler");
            } else {
                setActiveAccordian("about");
            }
        }
    }, [setActiveAccordian])

    const handleBusinessNameChange = createSetter("name");
    const handleBusinessEmailChange = createSetter("leadsContact");
    const handleBusinessPhoneChange = createSetter("leadsContactPhone");
    const handleWebsiteChange = createSetter("website");
    const handleBusinessDescriptionChange = createSetter("businessDescription");
    const handlePrimaryColorChange = createSetter("primaryColor");

    const handleTemplateChange = useCallback((template) => {
        setCreateProps({
            ...createProps,
            template
        });
    }, [setCreateProps, createProps]);

    const handleCreateClick = useCallback(() => {
        onCreate(createProps as CreateProps);
    }, [createProps, onCreate]);


    const businessValidation = (): { disabled: boolean, guidance: string } => {

        if (!createProps?.name) {
            return { disabled: true, guidance: "Business name is required." };
        }

        if (createProps?.name?.length < 2) {
            return { disabled: true, guidance: "Business name is too short." };
        }

        if (createProps?.name?.length > 150) {
            return { disabled: true, guidance: "Business name is too long." };
        }

        if (!createProps?.leadsContact) {
            return { disabled: true, guidance: "Business email is required." };
        }

        if (!validEmail) {
            return { disabled: true, guidance: "Business email is not valid." };
        }

        if (!createProps?.leadsContactPhone) {
            return { disabled: true, guidance: "Business phone is required." };
        }

        if (!validPhone) {
            return { disabled: true, guidance: "Business phone is not valid." };
        }

        return { disabled: false, guidance: "" };

    }

    const aiValidation = (): { disabled: boolean, guidance: string } => {

        /* if (!createProps?.website) {
             return { disabled: true, guidance: "Business website is required." };
         } */

        if (!createProps?.template?.url) {
            return { disabled: true, guidance: "AI model is required." };
        }

        if (!createProps?.businessDescription) {
            return { disabled: true, guidance: "Business description is required." };
        }

        return { disabled: false, guidance: "" };
    }

    const themeValidation = (): { disabled: boolean, guidance: string } => {
        if (!createProps?.primaryColor) {
            return { disabled: true, guidance: "Theme color is required." };
        }

        return { disabled: false, guidance: "" };
    }

    const schedulingValidation = (): { disabled: boolean, guidance: string } => {
        return { disabled: false, guidance: "" };
    }

    // do a check to see if we got any data
    const copy = { ...initialProps };
    delete copy.website;
    delete copy.technologies;

    // clean out any empty values
    const values = Object.values(copy).filter(v => v !== undefined && v !== null && v !== "");
    const noInitialData: boolean = values.length === 0;

    const bizValid = businessValidation();
    const aiValid = aiValidation();
    const themeValid = themeValidation();
    const scheduleValid = schedulingValidation();

    const disabled = bizValid.disabled || aiValid.disabled || themeValid.disabled;

    // TODO: Not yet!
    const showSchedulerConfig = false; // beta;

    const getHeader = useCallback((): JSX.Element => {

        if (noInitialData) {
            return (
                <>
                    {initialProps.website && (
                        <PageAlert background="blue" >
                            <p>⚠️We had a hard time getting data from the website you provided: {initialProps.website}</p>
                            <p>If the website looks incorrect you can go back to change it and try again otherwise you please fill out the following fields to help us configure your assistant.</p>
                            <div style={{ marginTop: "10px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-around" }}>
                                <Button
                                    variation="primary"
                                    leftIcon="arrow_back"
                                    label={'Go Back'}
                                    onClick={onBack}
                                />
                            </div>
                        </PageAlert>
                    )}
                </>
            );
        } else {
            return (
                <p>We will now begin to configure your assistant. We have filled out some initial fields based on information you have already provided and what we found on your website. Please review and correct as necessary.</p >
            )
        }
    }, [initialProps.website, noInitialData, onBack]);

    return (
        <div style={{ ...style }} className={className}>
            {error ? (
                <PageAlert background="orange" >
                    <p>We had a problem, please try again.  If it continues, please email support at support@xapp.ai</p>
                    <p>Error: {error.message}</p>
                    <p>Details:</p>
                    <pre style={{ overflow: "scroll" }}>{error.error}</pre>
                    {/**We need a call to action for them to get help */}
                </PageAlert>
            ) : (<></>)}
            {(creating || loadingMessage) ? (
                <div style={{ padding: "20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <LoadingSpinner
                        textStyle={{ fontSize: "1em" }}
                        loadingText={creating ? creatingStatusMessage : loadingMessage}
                    />
                </div>
            ) : (
                <div>
                    <h3>Review</h3>
                    {getHeader()}
                    <div style={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", rowGap: "10px", flexDirection: "column", alignItems: "left", justifyContent: "left" }}>
                            <Accordion title={
                                <span style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                                    <span>1. About your business </span>
                                    {bizValid.disabled ? <span style={{ color: "red" }}>{bizValid.guidance}</span> : <span>✅</span>}
                                </span>
                            } expanded={activeAccordian === "about"} onChange={handleAccordianChange.bind(this, "about")}>
                                <div style={{ display: "flex", rowGap: "5px", flexDirection: "column", alignItems: "left", justifyContent: "left" }}>
                                    <Input
                                        variation="outlined"
                                        type={"text"}
                                        label={"Business Name"}
                                        required
                                        value={createProps?.name || ""}
                                        onChange={handleBusinessNameChange}
                                        helperText={"This should match the name of your business on your website and Google Business Profile."}
                                    />
                                    <EmailInput
                                        variation="outlined"
                                        label={"Business Email"}
                                        required
                                        helperText={"What email do you want to use for people to get in touch with your business"}
                                        value={createProps?.leadsContact || ""}
                                        onChange={handleBusinessEmailChange}
                                    />
                                    <PhoneNumberInput
                                        variation="outlined"
                                        label={"Business Phone"}
                                        required
                                        helperText={"What phone number do you want to use for people to get in touch with your business"}
                                        value={createProps?.leadsContactPhone || ""}
                                        onChange={handleBusinessPhoneChange}
                                    />
                                </div>
                                <div style={{ marginTop: "10px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-around" }}>
                                    <Button
                                        label={"Next Section"}
                                        variation="secondary"
                                        onClick={handleAccordianChange.bind(this, "about")}
                                    />
                                </div>
                            </Accordion>
                            <Accordion title={
                                <span style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                                    <span>2. Help train your AI </span>
                                    {aiValid.disabled ? <span style={{ color: "red" }}>{aiValid.guidance}</span> : <span>✅</span>}
                                </span>
                            } expanded={activeAccordian === "train"} onChange={handleAccordianChange.bind(this, "train")}>
                                <div style={{ display: "flex", rowGap: "5px", flexDirection: "column", alignItems: "left", justifyContent: "left" }}>
                                    <URLInput
                                        variation="outlined"
                                        disabled={!!initialProps?.website}
                                        label={"Business Website"}
                                        required={!!initialProps.website}
                                        value={createProps?.website || ""}
                                        onChange={handleWebsiteChange}
                                        helperText={"The main website for your business, we will also use information here to help train your AI"}
                                    />
                                    <div style={{ margin: "15px 0px" }}>
                                        <SmartDropdown
                                            slugLabel={createProps?.template?.name || "Select AI Model"}
                                            inputLabel={"Search available model"}
                                            options={TEMPLATES}
                                            onChange={handleTemplateChange}
                                        />
                                        <small>Select the appropriate AI model that corresponds to your business.  If you don't see your business represented, select Everything Else</small>
                                    </div>
                                    <Input
                                        variation="outlined"
                                        type={"text"}
                                        multiline
                                        label={"Business Description"}
                                        helperText={"Business description is used to train the AI about your business and the services you provide."}
                                        value={createProps?.businessDescription || ""}
                                        onChange={handleBusinessDescriptionChange}
                                        minRows={5}
                                        style={{ minHeight: "120px" }}
                                    />
                                </div>
                                <div style={{ marginTop: "10px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-around" }}>
                                    <Button
                                        label={"Next Section"}
                                        variation="secondary"
                                        onClick={handleAccordianChange.bind(this, "train")}
                                    />
                                </div>
                            </Accordion>

                            <Accordion title={
                                <span style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                                    <span>3. Theme</span>
                                    {themeValid.disabled ? <span style={{ color: "red" }}>{themeValid.guidance}</span> : <span>✅</span>}
                                </span>
                            } expanded={activeAccordian === "theme"} onChange={handleAccordianChange.bind(this, "theme")}>
                                <div style={{ display: "flex", rowGap: "5px", flexDirection: "column", alignItems: "left", justifyContent: "left" }}>
                                    <WidgetColorPicker
                                        label="Theme Color"
                                        overrideLabel="Change"
                                        defaultLabel="Revert"
                                        passBackDefault={true}
                                        defaultValue={initialProps?.primaryColor || "#3295d2"}
                                        onChange={handlePrimaryColorChange}
                                        value={createProps?.primaryColor}
                                    />
                                    <small>Click the color to change the theme color.  You can use the color picker to then pick a color from your website.</small>
                                </div>
                                {showSchedulerConfig ? (
                                    <div style={{ marginTop: "10px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-around" }}>
                                        <Button
                                            label={"Next Section"}
                                            variation="secondary"
                                            onClick={handleAccordianChange.bind(this, "theme")}
                                        />
                                    </div>
                                ) : <></>}
                            </Accordion>

                            {showSchedulerConfig ? (
                                <Accordion title={
                                    <span style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                                        <span>4. Configure Scheduler </span>
                                        {scheduleValid.disabled ? <span style={{ color: "red" }}>{scheduleValid.guidance}</span> : <span>✅</span>}
                                    </span>
                                } expanded={activeAccordian === "scheduler"} onChange={handleAccordianChange.bind(this, "scheduler")}>
                                    <h3>Config</h3>
                                </Accordion>
                            ) : <></>}
                        </div>

                        <div style={{ padding: "20px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                            {typeof onBack === "function" && (
                                <Button
                                    leftIcon="arrow_back"
                                    label={'Back'}
                                    variation="flat"
                                    onClick={onBack}
                                />
                            )}
                            <Button
                                rightIcon="arrow_forward"
                                label={`Done`}
                                variation="primary"
                                disabled={disabled}
                                onClick={handleCreateClick}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
}